import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DownloadIcon from '@mui/icons-material/Download';
import { useUser } from '../contexts/UserContext';
import { getOrCreateReferralLink } from '../services/referralService';
import QRCode from 'react-qr-code';

const ReferralLink: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user: userData } = useUser();
  const [copySuccess, setCopySuccess] = useState(false);
  const [shortenedLink, setShortenedLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateShortLink = async () => {
    if (!userData?.uid) {
      setError('User not authenticated');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const shortLink = await getOrCreateReferralLink(userData.uid);
      setShortenedLink(shortLink);
    } catch (err) {
      console.error('Error creating short link:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load referral link. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    generateShortLink();
  }, [userData?.uid]);

  const handleCopyLink = async () => {
    if (!shortenedLink) return;
    
    try {
      await navigator.clipboard.writeText(shortenedLink);
      setCopySuccess(true);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleCloseSnackbar = () => {
    setCopySuccess(false);
  };

  const handleRetry = () => {
    generateShortLink();
  };

  const handleDownloadQR = () => {
    // Create a canvas to render the QR code for download
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const size = 300;
    canvas.width = size;
    canvas.height = size;

    if (ctx) {
      // White background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);

      // Create QR code SVG
      const svg = document.querySelector('svg[data-testid="qr-code"]') as SVGElement;
      if (svg) {
        const svgData = new XMLSerializer().serializeToString(svg);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);
        
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, size, size);
          
          // Download
          const link = document.createElement('a');
          link.download = 'fosterway-referral-qr.png';
          link.href = canvas.toDataURL();
          link.click();
          
          URL.revokeObjectURL(svgUrl);
        };
        img.src = svgUrl;
      }
    }
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>

      {/* QR Code Section */}
      {shortenedLink && (
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
          <Card 
            sx={{ 
              p: 3, 
              textAlign: 'center',
              backgroundColor: '#f8f9fa',
              border: '2px solid #e9ecef',
              borderRadius: 2,
              maxWidth: 300
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <QrCodeIcon sx={{ mr: 1, color: '#2e7d32' }} />
                <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                  Scan to Share
                </Typography>
              </Box>
              
              {/* QR Code with Fosterway styling */}
              <Box 
                sx={{ 
                  position: 'relative',
                  display: 'inline-block',
                  p: 2,
                  backgroundColor: 'white',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                <QRCode
                  value={shortenedLink}
                  size={200}
                  style={{ 
                    height: 'auto',
                    maxWidth: '100%',
                    width: '100%'
                  }}
                  fgColor="#1976d2" // Fosterway blue for main QR code
                  bgColor="#ffffff"
                />
              </Box>
              
              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 2, 
                  color: '#666',
                  fontSize: '12px',
                  mb: 2
                }}
              >
                Scan with your phone camera to share this referral link
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center', width: '100%' }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadQR}
                  size="small"
                  fullWidth
                  sx={{
                    borderColor: '#2e7d32',
                    color: '#2e7d32',
                    '&:hover': {
                      borderColor: '#1b5e20',
                      backgroundColor: '#f1f8e9'
                    }
                  }}
                >
                  Download QR Code
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyLink}
                  size="small"
                  fullWidth
                  disabled={!shortenedLink}
                  sx={{
                    borderColor: '#2e7d32',
                    color: '#2e7d32',
                    '&:hover': {
                      borderColor: '#1b5e20',
                      backgroundColor: '#f1f8e9'
                    }
                  }}
                >
                  Copy Link
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Loading and Error States */}
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, p: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading referral link...
          </Typography>
        </Box>
      )}
      
      {error && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="outlined" onClick={handleRetry}>
            Try Again
          </Button>
        </Box>
      )}

      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReferralLink;
